import { Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Link as RouterLink } from 'react-router-dom';
import { useEntity } from '@backstage/plugin-catalog-react';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { useEffect, useState, useMemo } from 'react';
import { useApi, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { CodeSnippet } from '@backstage/core-components';
import { ProtoViewer, SymbolEntry } from '../proto-browser/ProtoViewer';

const useStyles = makeStyles(theme => ({
  link: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
    fontSize: '0.875rem',
  },
}));

// Module-level cache shared with ProtoBrowserPage
let cachedIndex: { symbols: Record<string, SymbolEntry> } | null = null;
let indexPromise: Promise<{ symbols: Record<string, SymbolEntry> }> | null =
  null;

function useProtoIndex(): { symbols: Record<string, SymbolEntry> } | null {
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const backendUrl = configApi.getString('backend.baseUrl');
  const [index, setIndex] = useState(cachedIndex);

  useEffect(() => {
    if (cachedIndex) {
      setIndex(cachedIndex);
      return;
    }
    if (!indexPromise) {
      indexPromise = fetchApi
        .fetch(`${backendUrl}/api/proto-browser/index.json`)
        .then(res => res.json())
        .then(data => {
          cachedIndex = data;
          return data;
        })
        .catch(() => {
          indexPromise = null;
          return null;
        });
    }
    indexPromise.then(data => {
      if (data) setIndex(data);
    });
  }, [fetchApi, backendUrl]);

  return index;
}

function useProtoFile(filePath: string | undefined): string | null {
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const backendUrl = configApi.getString('backend.baseUrl');
  const [content, setContent] = useState<string | null>(null);

  useEffect(() => {
    if (!filePath) {
      setContent(null);
      return;
    }
    setContent(null);
    fetchApi
      .fetch(`${backendUrl}/api/proto-browser/file/${filePath}`)
      .then(res => (res.ok ? res.text() : null))
      .then(text => setContent(text))
      .catch(() => setContent(null));
  }, [fetchApi, filePath, backendUrl]);

  return content;
}

/**
 * Gets the proto symbol FQN from entity annotations.
 * Checks playgoals.com/proto-service, playgoals.com/proto-event, etc.
 */
function getProtoSymbol(
  annotations: Record<string, string>,
): string | undefined {
  return (
    annotations['playgoals.com/proto-service'] ??
    annotations['playgoals.com/proto-event'] ??
    undefined
  );
}

/**
 * Renders proto source with the ProtoViewer (syntax highlighting + clickable types)
 * and a "View in Proto Browser →" link. Used for grpc, event, and sub-event widgets.
 */
export function ProtoDefinitionWithBrowser({
  definition,
  label,
}: {
  definition: string;
  label?: string;
}) {
  const classes = useStyles();
  const { entity } = useEntity();
  const annotations = (entity.metadata.annotations ?? {}) as Record<
    string,
    string
  >;
  const symbol = getProtoSymbol(annotations);
  const index = useProtoIndex();
  const symbolEntry = symbol && index ? index.symbols[symbol] : undefined;
  const fileContent = useProtoFile(symbolEntry?.file);

  const currentPackage = useMemo(() => {
    if (!symbol) return '';
    const parts = symbol.split('.');
    parts.pop();
    return parts.join('.');
  }, [symbol]);

  // Carry the entity along so the browser can offer a way back to it. The
  // query has to precede the fragment, or it becomes part of the fragment and
  // the symbol anchor stops resolving.
  const browserUrl = symbolEntry
    ? `/proto-browser/${symbolEntry.file}?from=${encodeURIComponent(
        stringifyEntityRef(entity),
      )}#${symbol}`
    : undefined;

  // If we have the full file + index, use ProtoViewer for interactive rendering
  if (fileContent && index && symbolEntry) {
    return (
      <Box>
        {browserUrl && (
          <Typography variant="body2" className={classes.link}>
            <RouterLink to={browserUrl}>View in Proto Browser →</RouterLink>
          </Typography>
        )}
        <Typography variant="subtitle2" gutterBottom>
          {label ?? 'Definition'}
        </Typography>
        <ProtoViewer
          content={fileContent}
          filePath={symbolEntry.file}
          symbols={index.symbols}
          currentPackage={currentPackage}
          highlightSymbol={symbol}
          showOnlyHighlighted
        />
      </Box>
    );
  }

  // Fallback: show the raw definition with CodeSnippet
  return (
    <Box>
      {browserUrl && (
        <Typography variant="body2" className={classes.link}>
          <RouterLink to={browserUrl}>View in Proto Browser →</RouterLink>
        </Typography>
      )}
      <Typography variant="subtitle2" gutterBottom>
        {label ?? 'Definition'}
      </Typography>
      <CodeSnippet
        text={definition}
        language="protobuf"
        showLineNumbers
        showCopyCodeButton
      />
    </Box>
  );
}
